/**
 * BilliardKnockback — velocity-based knockback with wall bounces and enemy-enemy momentum transfer.
 *
 * Instead of teleporting enemies to their final knockback position, this system gives them
 * a velocity and simulates each frame: friction decay, wall reflection, elastic collisions
 * with other enemies (chain reactions), and hazard detection.
 *
 * Usage:
 *   BilliardKnockback.instance.applyKnockback(enemy, vx, vy, 'mine');
 *   // In WaveManager.update(): billiard.update(dt, aliveEnemies);
 */

import { Enemy } from '@/entities/Enemy';
import { TILE_SIZE, TileType, KNOCKBACK_IMMUNE_MASS, DECOR_INTERACTIONS, DecorInteraction } from '@/config/GameConfig';
import { MAP_GRID, getDecorAt } from '@/map/MapData';
import { GOD } from '@/config/GodMode';
import { EventBus, GameEvents } from '@/events/EventBus';
import { SpatialHash } from '@/systems/SpatialHash';

export class BilliardKnockback {
  static instance: BilliardKnockback;

  /** Enemies currently in knockback state */
  private active: Set<Enemy> = new Set();
  /** Spatial hash for collision queries during billiard sim */
  private hash: SpatialHash<Enemy>;

  constructor() {
    this.hash = new SpatialHash<Enemy>(64);
    BilliardKnockback.instance = this;
  }

  // ─────────────────────── Public API ───────────────────────

  /**
   * Inject knockback velocity on an enemy.
   * @param enemy - target enemy
   * @param vx - initial velocity X (px/s) — already direction-aware
   * @param vy - initial velocity Y (px/s)
   * @param source - what caused the knockback (for events)
   * @param chainDepth - chain propagation depth (0 = direct hit)
   */
  applyKnockback(enemy: Enemy, vx: number, vy: number, source: string, chainDepth = 0): void {
    if (!enemy.alive || enemy.isFlyer) return;

    // Angular spread: add random deviation to direction (fan effect)
    const spreadMin = GOD.billiardSpreadMinDeg * (Math.PI / 180);
    const spreadMax = GOD.billiardSpreadMaxDeg * (Math.PI / 180);
    const spreadAngle = (spreadMin + Math.random() * (spreadMax - spreadMin)) * (Math.random() < 0.5 ? -1 : 1);
    const cos = Math.cos(spreadAngle);
    const sin = Math.sin(spreadAngle);
    const svx = vx * cos - vy * sin;
    const svy = vx * sin + vy * cos;

    // Heavy resist: reduce velocity for massive enemies
    let resistMult = 1;
    if (enemy.mass >= KNOCKBACK_IMMUNE_MASS) {
      resistMult = GOD.billiardHeavyResistMult;
    }

    if (enemy.isKnockedBack) {
      // Already in knockback — stack velocities (juggle)
      enemy.kbVx += svx * resistMult;
      enemy.kbVy += svy * resistMult;
    } else {
      // Fresh knockback
      enemy.kbVx = svx * resistMult;
      enemy.kbVy = svy * resistMult;
      enemy.kbBounceCount = 0;
      enemy.kbSimTime = 0;
      enemy.isKnockedBack = true;
      // Save rotation for restoration after knockback
      (enemy as any).kbPrevRotation = enemy.body.rotation;
    }
    enemy.kbChainDepth = Math.max(enemy.kbChainDepth, chainDepth);

    this.active.add(enemy);

    EventBus.emit(GameEvents.BILLIARD_KNOCKBACK_START, {
      enemy,
      vx: enemy.kbVx,
      vy: enemy.kbVy,
      source,
    });
  }

  /**
   * Main simulation tick — call from WaveManager.update() after enemy.update().
   * @param dt - delta time in seconds
   * @param allEnemies - all alive enemies (for collision detection against stationary ones)
   */
  update(dt: number, allEnemies: Enemy[]): void {
    if (this.active.size === 0) return;

    // Rebuild spatial hash with ALL alive enemies (stationary ones can be hit)
    this.hash.rebuild(allEnemies, e => e.body.x, e => e.body.y);

    // Step each active enemy's knockback physics
    for (const enemy of this.active) {
      if (!enemy.alive) continue;
      this.stepEnemy(enemy, dt);
    }

    // Resolve collisions between knocked-back enemies and neighbors
    this.resolveCollisions(allEnemies);

    // Clean up settled enemies
    const settled: Enemy[] = [];
    for (const enemy of this.active) {
      if (!enemy.alive) {
        settled.push(enemy);
        continue;
      }
      const speed = Math.sqrt(enemy.kbVx * enemy.kbVx + enemy.kbVy * enemy.kbVy);
      enemy.kbSimTime += dt;
      if (speed < GOD.billiardMinSpeed || enemy.kbSimTime > GOD.billiardMaxSimTime || enemy.kbBounceCount > GOD.billiardMaxBounces) {
        settled.push(enemy);
      }
    }

    for (const enemy of settled) {
      this.endKnockback(enemy);
    }
  }

  /** Number of enemies currently in knockback (for debug/profiling) */
  get activeCount(): number { return this.active.size; }

  // ─────────────────────── Internal ───────────────────────

  /** Step one enemy's knockback physics for one frame.
   *  Uses tile-stepping raycast so fast enemies can never skip over walls/water. */
  private stepEnemy(enemy: Enemy, dt: number): void {
    const mapRows = MAP_GRID.length;
    const mapCols = MAP_GRID[0]?.length ?? 0;
    const maxPx = TILE_SIZE * 0.5; // margin from edge

    // ── Hard safety: if enemy is already out of walkable area, push it back ──
    {
      const c = Math.floor(enemy.body.x / TILE_SIZE);
      const r = Math.floor(enemy.body.y / TILE_SIZE);
      const safeTile = MAP_GRID[r]?.[c];
      if (c < 0 || c >= mapCols || r < 0 || r >= mapRows ||
          (!this.isTileWalkable(safeTile) && safeTile !== TileType.WATER && safeTile !== TileType.MAGMA)) {
        // Find nearest walkable tile and snap
        enemy.kbVx = 0;
        enemy.kbVy = 0;
        this.snapToNearestWalkable(enemy);
        return;
      }
    }

    // ── Water/magma drag: heavy friction when sliding on hazard tile ──
    const curCol = Math.floor(enemy.body.x / TILE_SIZE);
    const curRow = Math.floor(enemy.body.y / TILE_SIZE);
    const curTile = MAP_GRID[curRow]?.[curCol];
    if (curTile === TileType.WATER || curTile === TileType.MAGMA) {
      // Massive drag — enemy slows to a stop in the liquid
      enemy.kbVx *= 0.85;
      enemy.kbVy *= 0.85;
      const spd = Math.sqrt(enemy.kbVx * enemy.kbVx + enemy.kbVy * enemy.kbVy);
      if (spd < 20) {
        // Stopped in liquid → die
        const tileCX = curCol * TILE_SIZE + TILE_SIZE / 2;
        const tileCY = curRow * TILE_SIZE + TILE_SIZE / 2;
        enemy.body.setPosition(tileCX, tileCY);
        enemy.body.rotation = (enemy as any).kbPrevRotation ?? 0;
        enemy.kbVx = 0;
        enemy.kbVy = 0;
        enemy.isKnockedBack = false;
        this.active.delete(enemy);
        (enemy as any).startTerrainDeath(curTile === TileType.WATER ? 'water' : 'magma');
        return;
      }
      // Still moving — continue sliding (no extra friction below)
    }

    // Friction: multiplicative decay
    const friction = Math.max(0, 1 - GOD.billiardFriction * dt);
    enemy.kbVx *= friction;
    enemy.kbVy *= friction;

    // Full displacement this frame
    const dx = enemy.kbVx * dt;
    const dy = enemy.kbVy * dt;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 0.1) return; // negligible movement

    // Step size = half a tile (guarantees we never skip a tile)
    const stepSize = TILE_SIZE * 0.5;
    const steps = Math.ceil(dist / stepSize);
    const stepDx = dx / steps;
    const stepDy = dy / steps;

    let cx = enemy.body.x;
    let cy = enemy.body.y;

    for (let i = 0; i < steps; i++) {
      const nx = cx + stepDx;
      const ny = cy + stepDy;
      const result = this.checkTile(enemy, cx, cy, nx, ny, mapRows, mapCols);

      if (result === 'die') {
        return;
      }
      if (result === 'blocked') {
        enemy.body.setPosition(cx, cy);
        return;
      }
      if (result === 'slide') {
        // Entered hazard tile (water/magma) — accept position but stop stepping
        cx = nx;
        cy = ny;
        break;
      }
      // 'ok' — move to next position
      cx = nx;
      cy = ny;
    }

    // ── Hard pixel clamp: never allow position outside map pixel bounds ──
    cx = Math.max(maxPx, Math.min(mapCols * TILE_SIZE - maxPx, cx));
    cy = Math.max(maxPx, Math.min(mapRows * TILE_SIZE - maxPx, cy));

    // Verify final tile is walkable (belt + suspenders)
    const fCol = Math.floor(cx / TILE_SIZE);
    const fRow = Math.floor(cy / TILE_SIZE);
    if (this.isTileBlocked(fRow, fCol)) {
      enemy.kbVx = 0;
      enemy.kbVy = 0;
      return;
    }

    enemy.body.setPosition(cx, cy);

    // Visual spin proportional to speed
    const speed = Math.sqrt(enemy.kbVx * enemy.kbVx + enemy.kbVy * enemy.kbVy);
    const spinDir = enemy.kbVx > 0 ? 1 : -1;
    enemy.body.rotation += spinDir * speed * 0.003 * dt;
  }

  /** Snap enemy to the nearest walkable tile center */
  private snapToNearestWalkable(enemy: Enemy): void {
    const mapRows = MAP_GRID.length;
    const mapCols = MAP_GRID[0]?.length ?? 0;
    const ec = Math.floor(enemy.body.x / TILE_SIZE);
    const er = Math.floor(enemy.body.y / TILE_SIZE);
    let bestDist = Infinity;
    let bestX = enemy.body.x;
    let bestY = enemy.body.y;
    const searchRadius = 5;
    for (let r = Math.max(0, er - searchRadius); r < Math.min(mapRows, er + searchRadius + 1); r++) {
      for (let c = Math.max(0, ec - searchRadius); c < Math.min(mapCols, ec + searchRadius + 1); c++) {
        if (this.isTileWalkable(MAP_GRID[r]?.[c])) {
          const tx = c * TILE_SIZE + TILE_SIZE / 2;
          const ty = r * TILE_SIZE + TILE_SIZE / 2;
          const d = (tx - enemy.body.x) ** 2 + (ty - enemy.body.y) ** 2;
          if (d < bestDist) {
            bestDist = d;
            bestX = tx;
            bestY = ty;
          }
        }
      }
    }
    enemy.body.setPosition(bestX, bestY);
  }

  /** Check if moving from (cx,cy) to (nx,ny) hits anything.
   *  Returns 'ok', 'blocked' (velocity reflected), 'slide' (entered water/magma — accept pos but stop stepping), or 'die'. */
  private checkTile(enemy: Enemy, cx: number, cy: number, nx: number, ny: number, mapRows: number, mapCols: number): 'ok' | 'blocked' | 'slide' | 'die' {
    const col = Math.floor(nx / TILE_SIZE);
    const row = Math.floor(ny / TILE_SIZE);

    // Out of grid → reflect off map border
    if (col < 0 || col >= mapCols || row < 0 || row >= mapRows) {
      if (col < 0 || col >= mapCols) enemy.kbVx = -enemy.kbVx * GOD.billiardWallDamping;
      if (row < 0 || row >= mapRows) enemy.kbVy = -enemy.kbVy * GOD.billiardWallDamping;
      enemy.kbBounceCount++;
      this.emitWallBounce(enemy, cx, cy);
      return 'blocked';
    }

    const tile = MAP_GRID[row]?.[col];
    const decor = getDecorAt(col, row);

    // Hazardous tiles (water/magma): let enemy ENTER the tile but stop stepping this frame
    // Drag + death handled in stepEnemy's per-frame check on subsequent frames
    if (tile === TileType.WATER || tile === TileType.MAGMA) {
      return 'slide';
    }

    // Kill decorations (chasm): immediate death
    if (decor && DECOR_INTERACTIONS[decor.type]?.interaction === DecorInteraction.KILL) {
      const tileCX = col * TILE_SIZE + TILE_SIZE / 2;
      const tileCY = row * TILE_SIZE + TILE_SIZE / 2;
      enemy.body.setPosition(tileCX, tileCY);
      enemy.body.rotation = (enemy as any).kbPrevRotation ?? 0;
      enemy.kbVx = 0;
      enemy.kbVy = 0;
      enemy.isKnockedBack = false;
      this.active.delete(enemy);
      (enemy as any).startTerrainDeath('chasm');
      return 'die';
    }

    // Wall, blocking decoration, or any non-walkable tile → reflect
    const blocked = tile === undefined || tile === TileType.WALL ||
      (decor && DECOR_INTERACTIONS[decor.type]?.interaction === DecorInteraction.BLOCK) ||
      !this.isTileWalkable(tile);

    if (blocked) {
      const prevCol = Math.floor(cx / TILE_SIZE);
      const prevRow = Math.floor(cy / TILE_SIZE);

      if (col !== prevCol && row !== prevRow) {
        // Diagonal collision — determine which axis is blocked
        const xBlocked = this.isTileBlocked(prevRow, col);
        const yBlocked = this.isTileBlocked(row, prevCol);
        if (xBlocked) enemy.kbVx = -enemy.kbVx * GOD.billiardWallDamping;
        if (yBlocked) enemy.kbVy = -enemy.kbVy * GOD.billiardWallDamping;
        if (!xBlocked && !yBlocked) {
          enemy.kbVx = -enemy.kbVx * GOD.billiardWallDamping;
          enemy.kbVy = -enemy.kbVy * GOD.billiardWallDamping;
        }
      } else if (col !== prevCol) {
        enemy.kbVx = -enemy.kbVx * GOD.billiardWallDamping;
      } else if (row !== prevRow) {
        enemy.kbVy = -enemy.kbVy * GOD.billiardWallDamping;
      } else {
        // Same tile as current — shouldn't happen, but reflect both
        enemy.kbVx = -enemy.kbVx * GOD.billiardWallDamping;
        enemy.kbVy = -enemy.kbVy * GOD.billiardWallDamping;
      }

      enemy.kbBounceCount++;
      this.emitWallBounce(enemy, cx, cy);
      return 'blocked';
    }

    return 'ok';
  }

  /** Check if a tile position is blocked (wall, blocking decoration, undefined, or non-walkable).
   *  Water/magma are NOT blocked — enemies slide into them. */
  private isTileBlocked(row: number, col: number): boolean {
    if (row < 0 || row >= MAP_GRID.length || col < 0 || col >= (MAP_GRID[0]?.length ?? 0)) return true;
    const tile = MAP_GRID[row]?.[col];
    if (tile === undefined || tile === TileType.WALL) return true;
    // Water/magma are hazards, not walls — enemies must slide in, not bounce off
    if (tile === TileType.WATER || tile === TileType.MAGMA) return false;
    if (!this.isTileWalkable(tile)) return true;
    const decor = getDecorAt(col, row);
    if (decor && DECOR_INTERACTIONS[decor.type]?.interaction === DecorInteraction.BLOCK) return true;
    return false;
  }

  /** Check if a tile type is walkable for ground enemies (matches WaveManager) */
  private isTileWalkable(tile: TileType | undefined): boolean {
    return tile === TileType.PATH || tile === TileType.PLACEMENT ||
      tile === TileType.BROKEN_PLACEMENT || tile === TileType.BASE ||
      tile === TileType.SPAWN || tile === TileType.MUD ||
      tile === TileType.GRILLE;
  }

  /** Resolve collisions between knocked-back enemies and their neighbors */
  private resolveCollisions(allEnemies: Enemy[]): void {
    const resolved = new Set<string>();
    const QUERY_R = 50; // slightly larger than typical collision radii sum

    for (const a of this.active) {
      if (!a.alive) continue;
      const speed = Math.sqrt(a.kbVx * a.kbVx + a.kbVy * a.kbVy);
      if (speed < 1) continue; // not worth checking near-stationary

      const neighbors = this.hash.queryRadius(a.body.x, a.body.y, QUERY_R);
      for (const b of neighbors) {
        if (a === b || !b.alive) continue;
        if (a.isFlyer !== b.isFlyer) continue; // ground only hits ground

        // Deduplicate pairs
        const pairKey = a.id < b.id ? `${a.id}_${b.id}` : `${b.id}_${a.id}`;
        if (resolved.has(pairKey)) continue;
        resolved.add(pairKey);

        const dx = b.body.x - a.body.x;
        const dy = b.body.y - a.body.y;
        const distSq = dx * dx + dy * dy;
        const minDist = a.collisionRadius + b.collisionRadius;

        if (distSq < minDist * minDist && distSq > 0.01) {
          this.resolveCollision(a, b, dx, dy, distSq, minDist);
        }
      }
    }
  }

  /** Elastic collision with mass-based momentum transfer */
  private resolveCollision(a: Enemy, b: Enemy, dx: number, dy: number, distSq: number, minDist: number): void {
    const dist = Math.sqrt(distSq);
    const nx = dx / dist;
    const ny = dy / dist;

    // Separate overlapping bodies
    const overlap = minDist - dist;
    const totalMass = a.mass + b.mass;
    const pushA = overlap * (b.mass / totalMass);
    const pushB = overlap * (a.mass / totalMass);
    a.body.setPosition(a.body.x - nx * pushA, a.body.y - ny * pushA);
    b.body.setPosition(b.body.x + nx * pushB, b.body.y + ny * pushB);

    // Get velocities (stationary enemies have 0 knockback velocity)
    const aVx = a.kbVx, aVy = a.kbVy;
    const bVx = b.isKnockedBack ? b.kbVx : 0;
    const bVy = b.isKnockedBack ? b.kbVy : 0;

    // Relative velocity along collision normal
    const relVn = (bVx - aVx) * nx + (bVy - aVy) * ny;
    if (relVn >= 0) return; // separating, no collision

    // Elastic collision impulse (mass-weighted)
    const impulse = (2 * relVn) / (1 / a.mass + 1 / b.mass);

    // Apply impulse
    a.kbVx += (impulse / a.mass) * nx * GOD.billiardEnemyDamping;
    a.kbVy += (impulse / a.mass) * ny * GOD.billiardEnemyDamping;

    const bImpX = -(impulse / b.mass) * nx * GOD.billiardEnemyDamping;
    const bImpY = -(impulse / b.mass) * ny * GOD.billiardEnemyDamping;

    // Activate stationary enemy if chain depth allows
    if (!b.isKnockedBack) {
      if (a.kbChainDepth < GOD.billiardMaxChainDepth) {
        this.applyKnockback(b, bImpX, bImpY, 'chain', a.kbChainDepth + 1);
      }
    } else {
      b.kbVx += bImpX;
      b.kbVy += bImpY;
    }

    // Collision damage (proportional to impact speed)
    const relSpeed = Math.abs(relVn);
    if (GOD.billiardCollisionDamage > 0 && relSpeed > 30) {
      const dmgA = relSpeed * GOD.billiardCollisionDamage * a.mass / 50;
      const dmgB = relSpeed * GOD.billiardCollisionDamage * b.mass / 50;
      // Each enemy takes damage proportional to the OTHER's mass
      b.takeDamage(dmgA);
      a.takeDamage(dmgB);
    }

    // Emit collision event
    const midX = (a.body.x + b.body.x) / 2;
    const midY = (a.body.y + b.body.y) / 2;
    EventBus.emit(GameEvents.BILLIARD_ENEMY_COLLISION, {
      enemyA: a,
      enemyB: b,
      x: midX,
      y: midY,
      relativeSpeed: relSpeed,
    });
  }

  /** End knockback for an enemy: reset state, recalc path */
  private endKnockback(enemy: Enemy): void {
    this.active.delete(enemy);
    enemy.kbVx = 0;
    enemy.kbVy = 0;
    enemy.isKnockedBack = false;
    enemy.kbBounceCount = 0;
    enemy.kbChainDepth = 0;
    enemy.kbSimTime = 0;

    if (enemy.alive) {
      // Restore rotation
      enemy.body.rotation = (enemy as any).kbPrevRotation ?? 0;
      // Recalculate pathfinding from new position
      enemy.markPathDirty();

      EventBus.emit(GameEvents.BILLIARD_KNOCKBACK_END, { enemy });
    }
  }

  /** Emit wall bounce event */
  private emitWallBounce(enemy: Enemy, x: number, y: number): void {
    const speed = Math.sqrt(enemy.kbVx * enemy.kbVx + enemy.kbVy * enemy.kbVy);
    EventBus.emit(GameEvents.BILLIARD_WALL_BOUNCE, {
      enemy,
      x,
      y,
      speed,
    });
  }
}
